import { useFormContext } from 'react-hook-form';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import { Badge } from '@components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@components/ui/radio-group';
import { Label } from '@components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@components/ui/select';
import { X } from 'lucide-react';

export default function DateSelectionSection() {
  const form = useFormContext();
  const watchEventDateType = form.watch('eventDateType');
  const watchEventDateRange = form.watch('eventDateRange');
  const watchIsFlexibleWithDates = form.watch('isFlexibleWithDates');
  const watchFlexibleDates = form.watch('flexibleDates');
  const watchFlexibleDateRange = form.watch('flexibleDateRange');
  
  const calculateDayCount = () => {
    if (!watchEventDateRange?.start || !watchEventDateRange?.end) return 0;
    const start = new Date(watchEventDateRange.start);
    const end = new Date(watchEventDateRange.end);
    return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  };
  
  const calculateFlexibleDayCount = () => {
    if (!watchFlexibleDateRange?.start || !watchFlexibleDateRange?.end) return 0;
    const start = new Date(watchFlexibleDateRange.start);
    const end = new Date(watchFlexibleDateRange.end);
    return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  };
  
  const handleAddFlexibleDate = (date: string) => {
    const current = watchFlexibleDates || [];
    if (!current.includes(date)) {
      form.setValue('flexibleDates', [...current, date].sort(), { shouldValidate: true });
    }
  };
  
  const handleRemoveFlexibleDate = (date: string) => {
    const current = watchFlexibleDates || [];
    form.setValue('flexibleDates', current.filter((d: string) => d !== date), { shouldValidate: true });
  };
  
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };
  
  return (
    <section className="pb-12 mb-12 border-b border-border">
      <div className="text-center mb-8">
        <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground block mb-3">
          Step 03
        </span>
        <h2 className="text-[28px] md:text-[32px] font-bold text-foreground uppercase tracking-[0.025em] mb-3">
          When is your event?
        </h2>
        <p className="text-sm text-muted-foreground">
          Tell us about your preferred dates
        </p>
      </div>
      
      <div className="space-y-8">
        {/* Part 1: Event Type Question */}
        <FormField
          control={form.control}
          name="eventDateType"
          rules={{ required: 'Please select event type' }}
          render={({ field }) => (
            <FormItem data-field={field.name}>
              <FormLabel className="text-sm font-semibold text-foreground">
                Is your event a one-day event or a multi-day event? <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={(value) => {
                    field.onChange(value);
                    // Clear date fields when switching types
                    if (value === 'one-day') {
                      form.setValue('eventDateRange', { start: null, end: null });
                    } else {
                      form.setValue('eventDate', null);
                    }
                    // Clear flexible dates
                    form.setValue('flexibleDates', []);
                    form.setValue('flexibleDateRange', { start: null, end: null });
                  }}
                  value={field.value}
                  className="grid grid-cols-1 md:grid-cols-2 gap-4"
                >
                  <div className={`flex items-start gap-4 p-4 border-2 rounded-[4px] cursor-pointer transition-all ${
                    field.value === 'one-day' ? 'border-foreground bg-muted/30' : 'border-border hover:border-foreground'
                  }`}>
                    <RadioGroupItem value="one-day" id="one-day" className="mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <Label htmlFor="one-day" className="text-base font-semibold cursor-pointer text-foreground">
                        One-day event
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        A single specific date (e.g., wedding on Saturday, September 15)
                      </p>
                    </div>
                  </div>
                  
                  <div className={`flex items-start gap-4 p-4 border-2 rounded-[4px] cursor-pointer transition-all ${
                    field.value === 'multi-day' ? 'border-foreground bg-muted/30' : 'border-border hover:border-foreground'
                  }`}>
                    <RadioGroupItem value="multi-day" id="multi-day" className="mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <Label htmlFor="multi-day" className="text-base font-semibold cursor-pointer text-foreground">
                        Multi-day event
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        A date range (e.g., 3-day conference September 1-3)
                      </p>
                    </div>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage className="text-[13px]" />
            </FormItem>
          )}
        />
        
        {/* Part 2: Date Picker (Conditional) */}
        {watchEventDateType === 'one-day' && (
          <div className="bg-muted/50 rounded-[4px] p-6">
            <FormField
              control={form.control}
              name="eventDate"
              rules={{ required: 'Event date is required' }}
              render={({ field }) => (
                <FormItem data-field={field.name}>
                  <FormLabel className="text-sm font-semibold text-foreground">
                    Event Date <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      min={new Date().toISOString().split('T')[0]}
                      max="2030-12-31"
                      {...field}
                      value={field.value || ''}
                      className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]"
                    />
                  </FormControl>
                  <FormMessage className="text-[13px]" />
                </FormItem>
              )}
            />
          </div>
        )}
        
        {watchEventDateType === 'multi-day' && (
          <div className="bg-muted/50 rounded-[4px] p-6 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="eventDateRange.start"
                rules={{ required: 'Start date is required' }}
                render={({ field }) => (
                  <FormItem data-field={field.name}>
                    <FormLabel className="text-sm font-semibold text-foreground">
                      Start Date <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        min={new Date().toISOString().split('T')[0]}
                        max="2030-12-31"
                        {...field}
                        value={field.value || ''}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          // Clear end date if it's before new start date
                          const end = form.getValues('eventDateRange.end');
                          if (end && e.target.value > end) {
                            form.setValue('eventDateRange.end', null);
                          }
                        }}
                        className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]"
                      />
                    </FormControl>
                    <FormMessage className="text-[13px]" />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="eventDateRange.end"
                rules={{
                  required: 'End date is required',
                  validate: (value) => {
                    const start = form.getValues('eventDateRange.start');
                    if (!start) return true;
                    if (!value) return 'End date is required';
                    if (value < start) return 'End date must be on or after start date';
                    return true;
                  }
                }}
                render={({ field }) => (
                  <FormItem data-field={field.name}>
                    <FormLabel className="text-sm font-semibold text-foreground">
                      End Date <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        min={watchEventDateRange?.start || new Date().toISOString().split('T')[0]}
                        max="2030-12-31"
                        {...field}
                        value={field.value || ''}
                        className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]"
                      />
                    </FormControl>
                    <FormMessage className="text-[13px]" />
                  </FormItem>
                )}
              />
            </div>
            
            {watchEventDateRange?.start && watchEventDateRange?.end && calculateDayCount() > 0 && (
              <div className="flex items-center gap-2 pt-2">
                <Badge variant="secondary" className="text-xs font-semibold bg-muted text-foreground px-3 py-1">
                  This covers {calculateDayCount()} day{calculateDayCount() !== 1 ? 's' : ''}
                </Badge>
              </div>
            )}
          </div>
        )}
        
        {/* Part 3: Flexible Dates Question */}
        {(watchEventDateType === 'one-day' || watchEventDateType === 'multi-day') && (
          <div className="pt-8 border-t border-border">
            <FormField
              control={form.control}
              name="isFlexibleWithDates"
              render={({ field }) => (
                <FormItem data-field={field.name}>
                  <FormLabel className="text-sm font-semibold text-foreground mb-4 block">
                    Are you flexible with dates?
                  </FormLabel>
                  <Select 
                    onValueChange={(value) => {
                      field.onChange(value);
                      // Clear flexible dates when changing to 'no'
                      if (value === 'no') {
                        form.setValue('flexibleDates', []);
                        form.setValue('flexibleDateRange', { start: null, end: null });
                      }
                    }} 
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]">
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="no" className="text-sm">No</SelectItem>
                      <SelectItem value="yes" className="text-sm">Yes</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription className="text-sm text-muted-foreground mt-1">
                    If your selected dates are unavailable, will you consider an alternative date?
                  </FormDescription>
                  <FormMessage className="text-[13px]" />
                </FormItem>
              )}
            />
          </div>
        )}
        
        {/* Part 4: Flexible Date Picker (Conditional) */}
        {watchIsFlexibleWithDates === 'yes' && watchEventDateType === 'one-day' && (
          <div className="bg-[#f0f8ff] rounded-[4px] p-6 border-l-[3px] border-l-foreground">
            <p className="text-[13px] text-muted-foreground italic mb-4">
              Provide alternative date(s) you'd be flexible to move to if needed
            </p>
            
            <FormField
              control={form.control}
              name="flexibleDates"
              render={() => (
                <FormItem data-field="flexibleDates">
                  <FormLabel className="text-sm font-semibold text-foreground">
                    Alternative Dates
                  </FormLabel>
                  <div className="space-y-4">
                    <Input
                      type="date"
                      min={new Date().toISOString().split('T')[0]}
                      max="2030-12-31"
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAddFlexibleDate(e.target.value);
                          e.target.value = '';
                        }
                      }}
                      className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)] bg-white"
                    />
                    
                    {watchFlexibleDates && watchFlexibleDates.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {watchFlexibleDates.map((date: string) => (
                          <Badge key={date} variant="default" className="text-sm flex items-center gap-2 font-semibold">
                            {formatDate(date)}
                            <button
                              type="button"
                              onClick={() => handleRemoveFlexibleDate(date)}
                              className="hover:text-destructive"
                              aria-label="Remove date"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <FormMessage className="text-[13px]" />
                </FormItem>
              )}
            />
          </div>
        )}
        
        {watchIsFlexibleWithDates === 'yes' && watchEventDateType === 'multi-day' && (
          <div className="bg-[#f0f8ff] rounded-[4px] p-6 border-l-[3px] border-l-foreground space-y-4">
            <p className="text-[13px] text-muted-foreground italic mb-4">
              Provide an alternative date range you'd be flexible to move to if needed
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="flexibleDateRange.start"
                render={({ field }) => (
                  <FormItem data-field={field.name}>
                    <FormLabel className="text-sm font-semibold text-foreground">
                      Alternative Start Date
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        min={new Date().toISOString().split('T')[0]}
                        max="2030-12-31"
                        {...field}
                        value={field.value || ''}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          // Clear end date if it's before new start date
                          const end = form.getValues('flexibleDateRange.end');
                          if (end && e.target.value > end) {
                            form.setValue('flexibleDateRange.end', null);
                          }
                        }}
                        className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)] bg-white"
                      />
                    </FormControl>
                    <FormMessage className="text-[13px]" />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="flexibleDateRange.end"
                rules={{
                  validate: (value) => {
                    const start = form.getValues('flexibleDateRange.start');
                    if (!start || !value) return true;
                    if (value < start) return 'End date must be on or after start date';
                    return true;
                  }
                }}
                render={({ field }) => (
                  <FormItem data-field={field.name}>
                    <FormLabel className="text-sm font-semibold text-foreground">
                      Alternative End Date
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        min={watchFlexibleDateRange?.start || new Date().toISOString().split('T')[0]}
                        max="2030-12-31"
                        {...field}
                        value={field.value || ''}
                        className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)] bg-white"
                      />
                    </FormControl>
                    <FormMessage className="text-[13px]" />
                  </FormItem>
                )}
              />
            </div>
            
            {watchFlexibleDateRange?.start && watchFlexibleDateRange?.end && calculateFlexibleDayCount() > 0 && (
              <div className="flex items-center gap-2 pt-2">
                <Badge variant="secondary" className="text-xs font-semibold bg-muted text-foreground px-3 py-1">
                  Alternative range covers {calculateFlexibleDayCount()} day{calculateFlexibleDayCount() !== 1 ? 's' : ''}
                </Badge>
              </div>
            )}
          </div>
        )}
        
        {/* Part 5: Recurring Event Question */}
        {(watchEventDateType === 'one-day' || watchEventDateType === 'multi-day') && (
          <div className="pt-8 border-t border-border">
            <FormField
              control={form.control}
              name="isRecurringEvent"
              rules={{ required: 'Please indicate if recurring' }}
              render={({ field }) => (
                <FormItem data-field={field.name}>
                  <FormLabel className="text-sm font-semibold text-foreground mb-4 block">
                    Is this a recurring event? <span className="text-destructive">*</span>
                  </FormLabel>
                  <Select 
                    onValueChange={(value) => {
                      field.onChange(value);
                      // Clear frequency when changing to 'no'
                      if (value === 'no') {
                        form.setValue('recurringFrequency', '');
                      }
                    }} 
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]">
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="no" className="text-sm">No</SelectItem>
                      <SelectItem value="yes" className="text-sm">Yes</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-[13px]" />
                </FormItem>
              )}
            />
            
            {form.watch('isRecurringEvent') === 'yes' && (
              <div className="mt-6">
                <FormField
                  control={form.control}
                  name="recurringFrequency"
                  rules={{ required: 'Recurring frequency is required' }}
                  render={({ field }) => (
                    <FormItem data-field={field.name}>
                      <FormLabel className="text-sm font-semibold text-foreground">
                        How frequent does your event occur? <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          placeholder="e.g., Weekly, Monthly, Every 3 months"
                          {...field}
                          className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]"
                        />
                      </FormControl>
                      <FormMessage className="text-[13px]" />
                    </FormItem>
                  )}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
